import { datadogRum } from '@datadog/browser-rum-slim';
import { DatadogAdsEvents } from '@eventbrite/ads';
import { CoreEnv } from '@eventbrite/context-gen';
import { Layout } from '@eventbrite/eds-layout';
import {
    withMainControls,
    withOverlayControls,
} from '@eventbrite/eds-structure';
import {
    addWindowListener,
    HAS_DOCUMENT,
    removeWindowListener,
} from '@eventbrite/eds-utils';
import {
    HAS_WINDOW,
    HAS_WINDOW_LISTENERS,
} from '@eventbrite/feature-detection';
import { getLocationSuggestions } from '@eventbrite/location-autocomplete';
import { addCustomRUMEvent } from '@eventbrite/real-user-monitoring';
import { trackEventFromState } from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useMount } from 'react-use';
import { SeasonalRibbon } from '../../../../components/SeasonalRibbon';
import {
    ENTITY_TYPE_MAP,
    EVENT_CARD_TYPE,
    LOCATION_DIALOG_CONTENT,
} from '../../../../constants';
import {
    GA_CITY_BROWSE_CATEGORY,
    GA_SCROLL_COMPLETE_LABEL,
    GA_SCROLL_DEPTH_ACTION,
    GA_SCROLL_NA_LABEL,
} from '../../../../constants/analytics';
import { trackCityBrowsePageView } from '../../../../redux/actions/analytics';
import {
    getUserCurrentLocation as getUserCurrentLocationAction,
    locationChange as locationChangeAction,
    searchLocationSuggestions as searchLocationSuggestionsAction,
} from '../../../../redux/actions/location';
import {
    BrandState,
    Bucket,
    BucketsContent,
    ConnectedProps,
    DiscoverState,
} from '../../../../types/index';
import { CityBrowseFooter } from '../../components/CityBrowseFooter';
import { CityBrowseHeader } from '../../components/CityBrowseHeader';
import { NightlifeTouchpoint } from '../../components/NightlifeTouchpoint';
import { getLocalizedBrandOverrides, getMaskState } from '../../utils';
import { BreadCrumbContainer } from '../BreadCrumbContainer';
import { CITY_BROWSE_PLACEMENT_NAME } from '../BrowseBucket/tracking';
import { MainContent, MainContentDataLayer } from '../MainContent';

const SCROLL_DEBOUNCE_TIME = 200;
const DEFAULT_BUCKET_SIZE = 8;

const trackScrollPosition = (trackGAEvent: Function) => {
    if (document.body.scrollHeight <= window.innerHeight + window.scrollY) {
        trackGAEvent({
            category: GA_CITY_BROWSE_CATEGORY,
            label: GA_SCROLL_COMPLETE_LABEL,
            action: GA_SCROLL_DEPTH_ACTION,
        });
    }
};

const handleScrollTracking = (trackGAEvent: Function) => {
    if (HAS_WINDOW && HAS_WINDOW_LISTENERS && HAS_DOCUMENT) {
        if (document.body.scrollHeight === window.innerHeight) {
            trackGAEvent({
                category: GA_CITY_BROWSE_CATEGORY,
                label: GA_SCROLL_NA_LABEL,
                action: GA_SCROLL_DEPTH_ACTION,
            });
        }
    }

    const debouncedTrackScrollPosition = debounce(
        trackScrollPosition.bind(null, trackGAEvent),
        SCROLL_DEBOUNCE_TIME,
    );

    addWindowListener('scroll', debouncedTrackScrollPosition);

    return debouncedTrackScrollPosition;
};

const updateBuckets = (buckets: Bucket[]) => {
    if (buckets !== undefined) {
        return buckets.reduce((acc: BucketsContent, bucket) => {
            const contentKey = ENTITY_TYPE_MAP[bucket.type];
            const maybeEvents = bucket[contentKey];
            const promotedEvents = bucket.promoted_events || [];

            if (
                bucket.type !== EVENT_CARD_TYPE ||
                isEmpty(promotedEvents) ||
                !maybeEvents
            ) {
                acc.push(bucket);

                return acc;
            }

            maybeEvents.unshift(...promotedEvents);

            // Not liking this but there is no variable with the bucket size coming in the data...
            if (maybeEvents.length > DEFAULT_BUCKET_SIZE) {
                maybeEvents.pop();
            }
            acc.push(bucket);

            return acc;
        }, []);
    }
    return [];
};

interface OwnProps {
    history: any;
    /*
     * Array of location suggestions for consumption by
     * the location switcher
     */
    locationSuggestions?: {
        suggestions?: $FixMe;
        hasSuggestions?: boolean;
    };
    showOverlay?: Function;
    buckets: BucketsContent;
    moreBuckets: BucketsContent;
    excludedBuckets: BucketsContent;
}

export const CityBrowseBaseScreen = (props: CityBrowseBaseScreenProps) => {
    const {
        env,
        brand = {} as BrandState,
        cityBlurb,
        country,
        currentPlace,
        domain,
        experiments,
        featureFlags,
        h1Prefix,
        history,
        handleLocationSelect,
        handleLocationSuggestionChange,
        isOnline,
        isWaitingForLocation,
        locationSuggestions,
        onTrackPageViewAction,
        locationDenied,
        shouldHideMask,
        trackGAEvent,
        handleUseCurrentLocation,
        isBotRequest,
        showOverlay,
        buckets,
        moreBuckets,
        excludedBuckets,
        placeId,
        slug,
    } = props;
    const {
        artistImageUrl,
        artistName,
        artistSocialLinks,
        artistSummary,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
        decorationTopLeftUrl,
        decorationTopRightUrl,
        headerImageUrl,
        middleImageUrl,
        middleSmallImageUrl,
        footerImageUrl,
        footerSmallImageUrl,
    } = brand;

    useMount(() => {
        onTrackPageViewAction();
    });

    useEffect(() => {
        const listener = handleScrollTracking(trackGAEvent);

        () => removeWindowListener('scroll', listener);
    }, [trackGAEvent]);

    useEffect(() => {
        if (locationDenied) {
            showOverlay?.('dialog', LOCATION_DIALOG_CONTENT);
        }
    }, [locationDenied, showOverlay]);

    const locationData = { slug, country, currentPlace, isOnline, placeId };

    const bucketsUpdatedWithPromotedEvents = updateBuckets(buckets);

    useDataDogAdTracking(buckets, env);

    return (
        <Layout>
            {featureFlags.enableSeasonalModule && slug && placeId && country ? (
                <SeasonalRibbon
                    locationSlug={slug}
                    placeId={placeId}
                    country={country}
                    browseSurface="citybrowse"
                />
            ) : null}
            <BreadCrumbContainer />
            <CityBrowseHeader
                isBotRequest={isBotRequest}
                locationSuggestions={locationSuggestions}
                handleLocationSuggestionChange={handleLocationSuggestionChange}
                handleLocationSelect={handleLocationSelect}
                currentPlace={currentPlace}
                cityBlurb={cityBlurb}
                h1Prefix={h1Prefix}
                cityImage={headerImageUrl}
                shouldHideMask={shouldHideMask}
                experiments={experiments}
                domain={domain}
                trackGAEvent={trackGAEvent}
                isUseCurrentLocationEnabled={featureFlags?.useCurrentLocation}
                handleUseCurrentLocation={() =>
                    handleUseCurrentLocation?.(history, true)
                }
                isOnline={isOnline}
                isWaitingForLocation={isWaitingForLocation}
                history={props.history}
            />
            {featureFlags.enableNightlifeTouchpointCityBrowse && (
                <NightlifeTouchpoint
                    currentPlace={currentPlace}
                    slug={slug || ''}
                    placeId={placeId || ''}
                    data-heap-id="nightlife_touchpoint_on_citybrowse"
                />
            )}
            <MainContentDataLayer
                placeId={placeId}
                isOnline={isOnline}
                buckets={bucketsUpdatedWithPromotedEvents}
                excludedBuckets={excludedBuckets}
                moreBuckets={moreBuckets}
            >
                <MainContent
                    middleImage={middleImageUrl || ''}
                    middleSmallImage={middleSmallImageUrl || ''}
                    decorationTopLeftUrl={decorationTopLeftUrl || ''}
                    decorationTopRightUrl={decorationTopRightUrl || ''}
                    decorationBottomLeftUrl={decorationBottomLeftUrl || ''}
                    decorationBottomRightUrl={decorationBottomRightUrl || ''}
                    featureFlags={featureFlags}
                    locationData={locationData}
                />
            </MainContentDataLayer>
            <CityBrowseFooter
                footerImage={footerImageUrl}
                footerSmallImage={footerSmallImageUrl}
                artistImage={artistImageUrl}
                artistSummary={artistSummary}
                artistName={artistName}
                artistSocialLinks={artistSocialLinks}
                experiments={experiments}
            />
        </Layout>
    );
};

function useDataDogAdTracking(
    cityBrowseSSRBuckets: BucketsContent = [],
    env: CoreEnv,
) {
    const isMobile = !!env?.isMobile;

    useEffect(() => {
        const bucketsPromotedEventsCount = cityBrowseSSRBuckets
            .filter((b) => b.type === 'event')
            .reduce((acc, b) => acc + (b.promoted_events?.length || 0), 0);

        if (bucketsPromotedEventsCount === 0) {
            return;
        }

        datadogRum.onReady(() => {
            addCustomRUMEvent(DatadogAdsEvents.SSRObtained, {
                value: bucketsPromotedEventsCount,
                app: 'web',
                is_mobile: isMobile,
                interface: CITY_BROWSE_PLACEMENT_NAME,
            });
        });
    }, [cityBrowseSSRBuckets, isMobile]);
}

export const _mapStateToProps = (
    state: DiscoverState,
    _ownProps?: OwnProps,
) => {
    const brand = getLocalizedBrandOverrides(
        state.brand,
        state.location,
        state.app.tld,
    );
    return {
        env: state.env,
        country: state.location.country,
        currentPlace: state.location.currentPlace,
        h1Prefix: state.location.h1Prefix,
        cityBlurb: state.location.cityBlurb,
        slug: state.location.slug,
        placeId: state.location.placeId,
        locationSuggestions: getLocationSuggestions(
            state.location,
            state.app.featureFlags?.useCurrentLocation,
        ),
        shouldHideMask: getMaskState(brand, state.location.isOnline),
        domain: state.app.domain,
        featureFlags: state.app.featureFlags,
        experiments: state.app.experiments,
        locationDenied: state.location.locationDenied,
        isWaitingForLocation: state.location.isWaitingForLocation,
        isOnline: state.location.isOnline,
        isBotRequest: state.app.isBotRequest,
        /*
         * Tracking event from state from @eventbrite/site-analytics
         */
        trackGAEvent: (action: any) =>
            trackEventFromState(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
        brand,
    };
};

const _mapDispatchToProps = (dispatch: Function, ownProps: OwnProps) => ({
    /*
     * Callback to display updated autocomplete suggestions
     */
    handleLocationSuggestionChange: (...args: [$FixMe]) =>
        dispatch(searchLocationSuggestionsAction(...args)),
    /*
     * Callback for handling changing of the selected metro
     */
    handleLocationSelect: (locationChangeObj = {}) =>
        dispatch(
            locationChangeAction({
                ...locationChangeObj,
                history: ownProps.history,
            }),
        ),
    onTrackPageViewAction: () => dispatch(trackCityBrowsePageView()),
    handleUseCurrentLocation: (...args: any) =>
        dispatch(getUserCurrentLocationAction(...args)),
});
type ReduxProps = ConnectedProps<
    typeof _mapStateToProps,
    typeof _mapDispatchToProps
>;
type CityBrowseBaseScreenProps = ReduxProps & OwnProps;
const ConnectedComponent = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(CityBrowseBaseScreen);

export const CityBrowseBaseScreenContainer = withOverlayControls(
    withMainControls(
        withRouter(ConnectedComponent as $FixMe) as $FixMe,
    ) as $FixMe,
);
